import { useState, useEffect, useCallback } from 'react';
import { Phone, Server, Layers, ArrowDownLeft, ArrowUpRight, AlertTriangle, RefreshCw, Users } from 'lucide-react';
import { getDashboardStats, fetchCdrs, listSbcs, listPlatforms } from '../services/portalApi';

function formatDuration(secs) {
  const s = Number(secs) || 0;
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
}

function formatTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

function CdrStatusBadge({ status, hangupCause }) {
  if (/busy/i.test(hangupCause || ''))
    return <span className="badge badge-green">Busy</span>;
  const s = (status || '').toLowerCase();
  if (s === 'answered' || s === 'completed')
    return <span className="badge badge-green">Answered</span>;
  if (s === 'missed' || s === 'no_answer')
    return <span className="badge badge-amber">Missed</span>;
  return <span className="badge badge-red">{hangupCause || status || 'Failed'}</span>;
}

export default function Dashboard({ onNav, user, viewAsCustomer }) {
  const [stats,     setStats]     = useState(null);
  const [cdrs,      setCdrs]      = useState([]);
  const [sbcs,      setSbcs]      = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [loading,   setLoading]   = useState(true);

  const cid = viewAsCustomer?.id ?? (user?.isAdmin ? null : user?.id);

  const load = useCallback(async () => {
    setLoading(true);
    const [statsRes, cdrsRes, sbcsRes, platformsRes] = await Promise.allSettled([
      getDashboardStats(cid),
      fetchCdrs({ limit: 6, ...(cid ? { customerId: cid } : {}) }),
      user?.isAdmin ? listSbcs() : Promise.resolve({ sbcs: [] }),
      listPlatforms(cid),
    ]);
    if (statsRes.status     === 'fulfilled') setStats(statsRes.value);
    if (cdrsRes.status      === 'fulfilled') setCdrs(cdrsRes.value.cdrs || []);
    if (sbcsRes.status      === 'fulfilled') setSbcs(sbcsRes.value.sbcs || []);
    if (platformsRes.status === 'fulfilled') setPlatforms(platformsRes.value.platforms || []);
    setLoading(false);
  }, [cid, user?.isAdmin]);

  useEffect(() => { load(); }, [load]);

  // Derive alerts from live data
  const alerts = [];
  if (sbcs.some(s => s.cpu_pct > 80))
    alerts.push({ id: 'cpu', msg: 'One or more SBCs have high CPU usage (>80%)' });
  if (sbcs.some(s => s.status !== 'active'))
    alerts.push({ id: 'sbc', msg: 'One or more SBCs are not in active state' });
  if (!loading && stats?.totalDids === 0)
    alerts.push({ id: 'dids', msg: 'No DIDs synced — use Number Inventory to sync from Voxbone' });

  const activeSbcCount     = sbcs.filter(s => s.status === 'active').length;
  const showSbcPanel        = user?.isAdmin;
  const isCustomerView      = !user?.isAdmin || !!viewAsCustomer;
  const configuredPlatforms = platforms.filter(p => p.hasCustomConfig);
  // Topology: show platforms with any form of routing (customer config, global URI, or did_routing entry)
  const topoPlats           = isCustomerView
    ? platforms.filter(p => p.hasCustomConfig || p.hasGlobalConfig || p.hasRouting)
    : platforms;

  return (
    <div className="content">
      {/* Alerts */}
      {alerts.map(a => (
        <div key={a.id} className="alert alert-warn">
          <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>{a.msg}</span>
        </div>
      ))}

      {/* Stat cards */}
      <div className="stats-grid">
        {/* DIDs */}
        <div className="stat-card">
          <div className="stat-header">
            <span className="stat-label">{cid ? 'My Numbers' : 'Total DIDs'}</span>
            <div className="stat-icon" style={{ background: 'var(--info-bg)' }}>
              <Phone size={18} color="var(--info)" />
            </div>
          </div>
          <div className="stat-value">{loading ? '—' : (stats?.totalDids ?? '—')}</div>
          <div className="stat-change up">
            {loading ? '' : isCustomerView
              ? `${stats?.teamsUsers ?? 0} Teams · ${stats?.zoomUsers ?? 0} Zoom assigned`
              : `${stats?.assignedDids ?? 0} assigned · ${stats?.unassignedDids ?? 0} free`}
          </div>
        </div>

        {/* Calls today */}
        <div className="stat-card">
          <div className="stat-header">
            <span className="stat-label">Calls Today</span>
            <div className="stat-icon" style={{ background: 'var(--ok-bg)' }}>
              <Phone size={18} color="var(--ok)" />
            </div>
          </div>
          <div className="stat-value">{loading ? '—' : (stats?.todayCalls ?? '—')}</div>
          <div className="stat-change up">
            {loading ? ''
              : isCustomerView
                ? `via your assigned numbers`
                : `${stats?.totalCalls ?? 0} total on record`}
          </div>
        </div>

        {/* SBCs (admin) or Platform Users (customer) */}
        {user?.isAdmin && !cid ? (
          <div className="stat-card">
            <div className="stat-header">
              <span className="stat-label">Active SBCs</span>
              <div className="stat-icon" style={{ background: '#3b076433' }}>
                <Server size={18} color="var(--purple)" />
              </div>
            </div>
            <div className="stat-value">{loading ? '—' : activeSbcCount}</div>
            <div className="stat-change up">{loading ? '' : `${sbcs.length} total`}</div>
          </div>
        ) : (
          <div className="stat-card">
            <div className="stat-header">
              <span className="stat-label">Platform Users</span>
              <div className="stat-icon" style={{ background: '#3b076433' }}>
                <Users size={18} color="var(--purple)" />
              </div>
            </div>
            <div className="stat-value">
              {loading ? '—' : ((stats?.teamsUsers ?? 0) + (stats?.zoomUsers ?? 0))}
            </div>
            <div className="stat-change up">
              {loading ? '' : `${stats?.teamsUsers ?? 0} Teams · ${stats?.zoomUsers ?? 0} Zoom`}
            </div>
          </div>
        )}

        {/* Customers (admin) or Configured Platforms (customer) */}
        <div className="stat-card">
          <div className="stat-header">
            <span className="stat-label">{!isCustomerView ? 'Customers' : 'Platforms Configured'}</span>
            <div className="stat-icon" style={{ background: 'var(--warn-bg)' }}>
              <Layers size={18} color="var(--warn)" />
            </div>
          </div>
          <div className="stat-value">
            {loading ? '—'
              : !isCustomerView
                ? (stats?.totalCustomers ?? '—')
                : configuredPlatforms.length}
          </div>
          <div className="stat-change up">
            {loading ? ''
              : !isCustomerView
                ? 'registered accounts'
                : configuredPlatforms.length === 0
                  ? `${platforms.length} platforms available`
                  : `${configuredPlatforms.filter(p => p.type === 'UC').length} UC · ${configuredPlatforms.filter(p => p.type === 'CC').length} CC`}
          </div>
        </div>
      </div>

      {/* Topology */}
      <div className="card mb-20">
        <div className="card-header">
          <div>
            <div className="card-title">Call Flow Topology</div>
            <div className="card-sub">
              {isCustomerView
                ? 'PSTN → Damovo Hosting Platform → UC / CC Platforms'
                : 'PSTN → Voxbone → AudioCodes SBC → UC / CC Platforms'}
            </div>
          </div>
          <span className="badge badge-green"><span className="dot green" /> Live</span>
        </div>
        <div className="topology">
          {/* PSTN */}
          <div className="topo-node">
            <div className="topo-icon" style={{ background: 'var(--border)', border: '1px solid var(--border-strong)' }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="var(--muted-2)" strokeWidth="1.5"/>
                <path d="M2 12h20M12 2a15.3 15.3 0 010 20M12 2a15.3 15.3 0 000 20" stroke="var(--muted-2)" strokeWidth="1.5"/>
              </svg>
            </div>
            <div className="topo-name">PSTN</div>
            <div className="topo-label">Public Switched Telephone Network</div>
          </div>

          <div className="topo-arrow">
            <div className="topo-line"><span className="topo-line-label">SIP Trunks</span></div>
          </div>

          {isCustomerView ? (
            /* Customer view: single Damovo Hosting Platform node */
            <div className="topo-node">
              <div className="topo-icon" style={{ background: 'linear-gradient(135deg, var(--brand-dark), var(--brand))', border: '1px solid #7a103544' }}>
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z" fill="var(--on-brand)"/>
                </svg>
              </div>
              <div className="topo-name">Damovo Hosting</div>
              <div className="topo-label">
                {loading ? 'Loading…' : `${stats?.totalDids ?? 0} numbers · ${activeSbcCount > 0 ? activeSbcCount + ' SBC active' : 'carrier platform'}`}
              </div>
            </div>
          ) : (
            /* Admin view: show Voxbone and SBC separately */
            <>
              <div className="topo-node">
                <div className="topo-icon" style={{ background: '#3a0010', border: '1px solid #47061e55' }}>
                  <span style={{ fontSize: 14, fontWeight: 900, color: 'var(--brand-text)' }}>VX</span>
                </div>
                <div className="topo-name">Voxbone</div>
                <div className="topo-label">
                  {loading ? 'Loading…' : `${stats?.totalDids ?? 0} DIDs · ${stats?.assignedDids ?? 0} assigned`}
                </div>
              </div>

              <div className="topo-arrow">
                <div className="topo-line"><span className="topo-line-label">SIP / TLS</span></div>
              </div>

              <div className="topo-node">
                <div className="topo-icon" style={{ background: 'var(--purple-bg)', border: '1px solid #6b21a833' }}>
                  <Server size={24} color="var(--purple)" />
                </div>
                <div className="topo-name">AudioCodes SBC</div>
                <div className="topo-label">
                  {loading ? 'Loading…' : `${activeSbcCount} of ${sbcs.length} active`}
                </div>
              </div>
            </>
          )}

          <div className="topo-arrow">
            <div className="topo-line"><span className="topo-line-label">SIP / SRTP</span></div>
          </div>

          {/* Platforms */}
          <div className="topo-node">
            {topoPlats.length === 0 ? (
              <div style={{ color: 'var(--muted-3)', fontSize: 12, textAlign: 'center', maxWidth: 140 }}>
                {isCustomerView
                  ? 'No platforms configured yet'
                  : 'No platforms available'}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {topoPlats.slice(0, 4).map(p => (
                  <div key={p.id} style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    background: 'var(--bg)', border: '1px solid var(--border)',
                    borderRadius: 8, padding: '5px 10px',
                  }}>
                    <span style={{
                      width: 22, height: 22, borderRadius: 4,
                      background: p.bg, color: p.color,
                      display: 'inline-flex', alignItems: 'center',
                      justifyContent: 'center', fontSize: 9, fontWeight: 800,
                    }}>{p.abbr}</span>
                    <span style={{ fontSize: 12, color: 'var(--muted)' }}>{p.name}</span>
                    <span className={`badge badge-${p.type === 'UC' ? 'green' : 'purple'}`} style={{ marginLeft: 4 }}>{p.type}</span>
                  </div>
                ))}
                {topoPlats.length > 4 && (
                  <div style={{ fontSize: 11, color: 'var(--muted-3)', paddingLeft: 4 }}>
                    +{topoPlats.length - 4} more
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recent calls + SBC health */}
      <div className={showSbcPanel ? 'grid-2' : ''}>
        {/* Recent Calls */}
        <div className="card">
          <div className="card-header">
            <div>
              <div className="card-title">Recent Calls</div>
              <div className="card-sub">Latest CDR records</div>
            </div>
            <button className="btn btn-secondary btn-xs" onClick={() => onNav('analytics')}>View all</button>
          </div>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted-3)' }}>
              <RefreshCw size={16} style={{ animation: 'spin 0.8s linear infinite' }}/>
            </div>
          ) : cdrs.length === 0 ? (
            <div style={{ padding: '32px 20px', textAlign: 'center', color: 'var(--muted-3)', fontSize: 13 }}>
              {isCustomerView
                ? 'No call records found for your account.'
                : 'No call records yet. Sync CDRs from the Analytics page.'}
            </div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Dir</th>
                    <th>Number</th>
                    <th>Duration</th>
                    <th>Status</th>
                    <th>Time</th>
                  </tr>
                </thead>
                <tbody>
                  {cdrs.map(c => (
                    <tr key={c.id}>
                      <td>
                        {c.direction === 'inbound'
                          ? <span className="call-direction call-in"><ArrowDownLeft size={12}/> In</span>
                          : <span className="call-direction call-out"><ArrowUpRight size={12}/> Out</span>
                        }
                      </td>
                      <td>
                        <span className="tag font-mono" style={{ fontSize: 11 }}>
                          {c.direction === 'inbound' ? c.from_number : c.to_number}
                        </span>
                      </td>
                      <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{formatDuration(c.duration)}</td>
                      <td><CdrStatusBadge status={c.status} hangupCause={c.hangup_cause} /></td>
                      <td style={{ color: 'var(--muted-3)', fontSize: 11 }}>{formatTime(c.started_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* SBC Health (admin only) */}
        {showSbcPanel && (
          <div className="card">
            <div className="card-header">
              <div>
                <div className="card-title">SBC Health</div>
                <div className="card-sub">AudioCodes instances</div>
              </div>
              <button className="btn btn-secondary btn-xs" onClick={() => onNav('sbc')}>Manage</button>
            </div>
            <div className="card-body">
              {loading ? (
                <div style={{ padding: 32, textAlign: 'center', color: 'var(--muted-3)' }}>
                  <RefreshCw size={16} style={{ animation: 'spin 0.8s linear infinite' }}/>
                </div>
              ) : sbcs.length === 0 ? (
                <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--muted-3)', fontSize: 13 }}>
                  No SBCs configured yet.
                </div>
              ) : (
                sbcs.map(sbc => {
                  const cpuPct = Number(sbc.cpu_pct) || 0;
                  const memPct = Number(sbc.mem_pct) || 0;
                  return (
                    <div key={sbc.id} style={{ marginBottom: 16 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span className={`dot ${sbc.status === 'active' ? 'green' : 'red'}`}/>
                          <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-2)' }}>{sbc.name}</span>
                          {sbc.model && (
                            <span className="badge badge-gray">{sbc.model.replace('AudioCodes ', '')}</span>
                          )}
                        </div>
                        <span style={{ fontSize: 11, color: 'var(--muted-2)' }}>
                          {sbc.active_calls || 0} / {sbc.max_calls || '—'} calls
                        </span>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                        <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                            <span style={{ fontSize: 10, color: 'var(--muted-3)' }}>CPU</span>
                            <span style={{ fontSize: 10, color: 'var(--muted)' }}>{cpuPct}%</span>
                          </div>
                          <div className="progress-bar">
                            <div className="progress-fill" style={{ width: `${cpuPct}%`, background: cpuPct > 70 ? 'var(--err)' : 'var(--purple)' }}/>
                          </div>
                        </div>
                        <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                            <span style={{ fontSize: 10, color: 'var(--muted-3)' }}>Memory</span>
                            <span style={{ fontSize: 10, color: 'var(--muted)' }}>{memPct}%</span>
                          </div>
                          <div className="progress-bar">
                            <div className="progress-fill" style={{ width: `${memPct}%`, background: memPct > 70 ? 'var(--err)' : 'var(--purple)' }}/>
                          </div>
                        </div>
                      </div>
                      {(sbc.location || sbc.public_ip) && (
                        <div style={{ fontSize: 11, color: 'var(--muted-3)', marginTop: 4 }}>
                          {[sbc.location, sbc.public_ip, sbc.uptime && `Up ${sbc.uptime}`].filter(Boolean).join(' · ')}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
